import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  getUsers, 
  createUser, 
  updateUser, 
  softDeleteUser, 
  restoreUser,
  adminResetPassword,
  getRolesPermissions 
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  User,
  Shield,
  Key,
  Power,
  Eye,
  EyeSlash,
  CheckCircle,
  XCircle,
  ArrowCounterClockwise,
  Users,
  Crown,
  Briefcase,
  UserCircle,
  Buildings,
  Clock,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// Role Badge Component
function RoleBadge({ role }) {
  const roleConfig = {
    admin: { color: 'bg-red-500/20 text-red-400', icon: Crown },
    gerencia: { color: 'bg-purple-500/20 text-purple-400', icon: Shield },
    pm: { color: 'bg-blue-500/20 text-blue-400', icon: Briefcase },
    rrhh: { color: 'bg-amber-500/20 text-amber-400', icon: Users },
    empleado: { color: 'bg-emerald-500/20 text-emerald-400', icon: UserCircle },
    cliente: { color: 'bg-cyan-500/20 text-cyan-400', icon: Buildings },
  };
  
  const config = roleConfig[role] || roleConfig.empleado;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {role}
    </Badge>
  );
}

// Permissions Matrix Component
function PermissionsMatrix({ permissions, roleFilter }) {
  const { isSpanish } = useLanguage();
  
  const permissionGroups = {
    dashboard: { label: 'Dashboard', permissions: ['dashboard.view', 'dashboard.financial'] },
    employees: { label: isSpanish ? 'Empleados' : 'Employees', permissions: ['employees.create', 'employees.edit', 'employees.delete', 'employees.view'] },
    users: { label: isSpanish ? 'Usuarios' : 'Users', permissions: ['users.create', 'users.edit', 'users.delete', 'users.view'] },
    projects: { label: isSpanish ? 'Proyectos' : 'Projects', permissions: ['projects.create', 'projects.edit', 'projects.delete', 'projects.view', 'projects.view_assigned', 'projects.view_own'] },
    tasks: { label: isSpanish ? 'Tareas' : 'Tasks', permissions: ['tasks.create', 'tasks.edit', 'tasks.delete', 'tasks.view', 'tasks.approve', 'tasks.view_own', 'tasks.update_own'] },
    time: { label: isSpanish ? 'Tiempo' : 'Time', permissions: ['time.log', 'time.approve', 'time.view_all', 'time.view_team', 'time.view_own'] },
    clients: { label: isSpanish ? 'Clientes' : 'Clients', permissions: ['clients.create', 'clients.edit', 'clients.delete', 'clients.view'] },
    billing: { label: isSpanish ? 'Facturación' : 'Billing', permissions: ['invoices.create', 'invoices.edit', 'invoices.view', 'invoices.view_own', 'quotations.create', 'quotations.edit', 'quotations.view', 'quotations.view_own'] },
    payments: { label: isSpanish ? 'Pagos' : 'Payments', permissions: ['payments.create', 'payments.approve', 'payments.view'] },
    reports: { label: isSpanish ? 'Reportes' : 'Reports', permissions: ['reports.view', 'reports.financial', 'accounting.view', 'accounting.manage'] },
  };
  
  const roles = roleFilter ? [roleFilter] : Object.keys(permissions);
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#242E42]">
            <th className="text-left py-3 px-4 text-[#9CA3AF] font-medium sticky left-0 bg-[#151B26]">{isSpanish ? 'Módulo' : 'Module'}</th>
            {roles.map(role => (
              <th key={role} className="text-center py-3 px-4 text-[#9CA3AF] font-medium">
                <RoleBadge role={role} />
                <div className="text-xs mt-1 font-normal">{permissions[role]?.description}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(permissionGroups).map(([groupKey, group]) => (
            <tr key={groupKey} className="border-b border-[#242E42]/50 hover:bg-[#0B0E14]/50">
              <td className="py-3 px-4 text-[#F3F4F6] font-medium sticky left-0 bg-[#151B26]">{group.label}</td>
              {roles.map(role => {
                const rolePerms = permissions[role]?.permissions || [];
                const hasAny = group.permissions.some(p => rolePerms.includes(p));
                const hasAll = group.permissions.every(p => rolePerms.includes(p));
                
                return (
                  <td key={role} className="text-center py-3 px-4">
                    {hasAll ? (
                      <CheckCircle className="w-5 h-5 text-emerald-400 mx-auto" />
                    ) : hasAny ? (
                      <div className="w-5 h-5 rounded-full border-2 border-amber-400 mx-auto flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                      </div>
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-600 mx-auto" />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex gap-6 mt-4 text-sm text-[#9CA3AF]">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-emerald-400" />
          <span>{isSpanish ? 'Acceso completo' : 'Full access'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full border-2 border-amber-400 flex items-center justify-center">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400"></div>
          </div>
          <span>{isSpanish ? 'Acceso parcial' : 'Partial access'}</span>
        </div>
        <div className="flex items-center gap-2">
          <XCircle className="w-4 h-4 text-gray-600" />
          <span>{isSpanish ? 'Sin acceso' : 'No access'}</span>
        </div>
      </div>
    </div>
  );
}

// User Form Dialog
function UserFormDialog({ user, open, onOpenChange, onSave }) {
  const { t, isSpanish } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'empleado',
    is_active: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role: user.role || 'empleado',
        is_active: user.is_active !== false,
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'empleado',
        is_active: true,
      });
    }
  }, [user, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { ...formData };
      if (user && !data.password) {
        delete data.password;
      }
      
      if (user) {
        await updateUser(user._id, data);
        toast.success(isSpanish ? 'Usuario actualizado' : 'User updated');
      } else {
        await createUser(data);
        toast.success(isSpanish ? 'Usuario creado' : 'User created');
      }
      onSave();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#151B26] border-[#242E42] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#F3F4F6]">
            {user ? (isSpanish ? 'Editar Usuario' : 'Edit User') : (isSpanish ? 'Nuevo Usuario' : 'New User')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-[#9CA3AF]">{t('name')}</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="valhalla-input"
              required
            />
          </div>
          <div>
            <Label className="text-[#9CA3AF]">Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="valhalla-input"
              required
            />
          </div>
          <div>
            <Label className="text-[#9CA3AF]">
              {t('password')} {user && <span className="text-xs">({isSpanish ? 'dejar vacío para no cambiar' : 'leave empty to keep'})</span>}
            </Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="valhalla-input pr-10"
                required={!user}
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#F3F4F6]"
              >
                {showPassword ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-[#9CA3AF]">{t('role')}</Label>
            <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
              <SelectTrigger className="valhalla-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="gerencia">Gerencia</SelectItem>
                <SelectItem value="pm">PM</SelectItem>
                <SelectItem value="rrhh">RRHH</SelectItem>
                <SelectItem value="empleado">Empleado</SelectItem>
                <SelectItem value="cliente">Cliente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-[#9CA3AF]">{isSpanish ? 'Cuenta Activa' : 'Active Account'}</Label>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 border-[#242E42]">
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">
              {loading ? t('loading') : t('save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Main Page Component
export function UsersPage() {
  const { t, isSpanish } = useLanguage();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [roleCounts, setRoleCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, permRes] = await Promise.all([
        getUsers({ role: roleFilter !== 'all' ? roleFilter : undefined, include_deleted: showDeleted }),
        getRolesPermissions()
      ]);
      setUsers(usersRes.data);
      setPermissions(permRes.data.permissions);
      setRoleCounts(permRes.data.role_counts);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [roleFilter, showDeleted, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = (user) => {
    setEditingUser(user);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingUser(null);
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!confirm(isSpanish ? '¿Archivar este usuario?' : 'Archive this user?')) return;
    try {
      await softDeleteUser(userId);
      toast.success(isSpanish ? 'Usuario archivado' : 'User archived');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleRestore = async (userId) => {
    try {
      await restoreUser(userId);
      toast.success(isSpanish ? 'Usuario restaurado' : 'User restored');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt(isSpanish ? 'Nueva contraseña (mín 6 caracteres):' : 'New password (min 6 chars):');
    if (!newPassword || newPassword.length < 6) {
      toast.error(isSpanish ? 'Contraseña inválida' : 'Invalid password');
      return;
    }
    try {
      await adminResetPassword(userId, { new_password: newPassword });
      toast.success(isSpanish ? 'Contraseña cambiada' : 'Password changed');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const filteredUsers = users.filter(u =>
    searchTerm === '' ||
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const roles = ['admin', 'gerencia', 'pm', 'rrhh', 'empleado', 'cliente'];

  return (
    <Layout title={isSpanish ? 'Gestión de Usuarios' : 'User Management'}>
      <div className="space-y-6" data-testid="users-page">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#0B0E14] border border-[#242E42] p-1">
            <TabsTrigger value="users" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <Users className="w-4 h-4 mr-2" />
              {isSpanish ? 'Usuarios' : 'Users'}
            </TabsTrigger>
            <TabsTrigger value="permissions" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <Shield className="w-4 h-4 mr-2" />
              {isSpanish ? 'Permisos por Rol' : 'Role Permissions'}
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6 space-y-4">
            {/* Role Filter Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <button
                onClick={() => setRoleFilter('all')}
                className={`p-4 rounded-xl border transition-all ${roleFilter === 'all' ? 'border-[#E2B05E] bg-[#E2B05E]/10' : 'border-[#242E42] bg-[#151B26] hover:border-[#3AB8D6]/50'}`}
              >
                <p className="text-2xl font-bold text-[#F3F4F6]">{Object.values(roleCounts).reduce((a, b) => a + b, 0)}</p>
                <p className="text-xs text-[#9CA3AF]">{isSpanish ? 'Todos' : 'All'}</p>
              </button>
              {roles.map(role => (
                <button
                  key={role}
                  onClick={() => setRoleFilter(role)}
                  className={`p-4 rounded-xl border transition-all ${roleFilter === role ? 'border-[#E2B05E] bg-[#E2B05E]/10' : 'border-[#242E42] bg-[#151B26] hover:border-[#3AB8D6]/50'}`}
                >
                  <p className="text-2xl font-bold text-[#F3F4F6]">{roleCounts[role] || 0}</p>
                  <p className="text-xs text-[#9CA3AF] capitalize">{role}</p>
                </button>
              ))}
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex gap-4 items-center">
                <div className="relative">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                  <Input
                    placeholder={isSpanish ? 'Buscar usuarios...' : 'Search users...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 valhalla-input w-64"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                  <Switch checked={showDeleted} onCheckedChange={setShowDeleted} />
                  {isSpanish ? 'Ver archivados' : 'Show archived'}
                </label>
              </div>
              <Button onClick={handleNew} className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">
                <Plus className="w-4 h-4 mr-2" />
                {isSpanish ? 'Nuevo Usuario' : 'New User'}
              </Button>
            </div>

            {/* Users Table */}
            {loading ? (
              <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>
            ) : filteredUsers.length === 0 ? (
              <div className="valhalla-card p-12 text-center">
                <User className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
                <p className="text-[#9CA3AF]">{t('noData')}</p>
              </div>
            ) : (
              <div className="valhalla-card overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#0B0E14]">
                    <tr>
                      <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Usuario' : 'User'}</th>
                      <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">Email</th>
                      <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{t('role')}</th>
                      <th className="text-center py-4 px-6 text-[#9CA3AF] font-medium">{t('status')}</th>
                      <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Último Acceso' : 'Last Login'}</th>
                      <th className="text-center py-4 px-6 text-[#9CA3AF] font-medium">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u._id} className={`border-t border-[#242E42] hover:bg-[#0B0E14]/50 ${u.is_deleted ? 'opacity-50' : ''}`}>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3AB8D6] to-[#2A8FA8] flex items-center justify-center text-white font-semibold">
                              {u.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || u.email?.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-[#F3F4F6] font-medium">{u.name || '-'}</span>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-[#9CA3AF]">{u.email}</td>
                        <td className="py-4 px-6"><RoleBadge role={u.role} /></td>
                        <td className="py-4 px-6 text-center">
                          {u.is_deleted ? (
                            <Badge className="bg-gray-500/20 text-gray-400">{isSpanish ? 'Archivado' : 'Archived'}</Badge>
                          ) : u.is_active ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400">{isSpanish ? 'Activo' : 'Active'}</Badge>
                          ) : (
                            <Badge className="bg-red-500/20 text-red-400">{isSpanish ? 'Inactivo' : 'Inactive'}</Badge>
                          )}
                        </td>
                        <td className="py-4 px-6 text-[#9CA3AF] text-sm">
                          {u.last_login ? new Date(u.last_login).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center justify-center gap-1">
                            {u.is_deleted ? (
                              <Button size="sm" variant="ghost" onClick={() => handleRestore(u._id)} className="text-[#3AB8D6] hover:text-[#F3F4F6]" title={isSpanish ? 'Restaurar' : 'Restore'}>
                                <ArrowCounterClockwise className="w-4 h-4" />
                              </Button>
                            ) : (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => handleEdit(u)} className="text-[#9CA3AF] hover:text-[#3AB8D6]" title={t('edit')}>
                                  <PencilSimple className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleResetPassword(u._id)} className="text-[#9CA3AF] hover:text-[#E2B05E]" title={isSpanish ? 'Reset Password' : 'Reset Password'}>
                                  <Key className="w-4 h-4" />
                                </Button>
                                {u._id !== currentUser?._id && (
                                  <Button size="sm" variant="ghost" onClick={() => handleDelete(u._id)} className="text-[#9CA3AF] hover:text-red-400" title={isSpanish ? 'Archivar' : 'Archive'}>
                                    <Trash className="w-4 h-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions" className="mt-6">
            <div className="valhalla-card p-6">
              <h3 className="text-lg font-semibold text-[#F3F4F6] mb-6 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#E2B05E]" />
                {isSpanish ? 'Matriz de Permisos por Rol' : 'Role Permissions Matrix'}
              </h3>
              <PermissionsMatrix permissions={permissions} roleFilter={null} />
            </div>

            {/* Individual Role Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {Object.entries(permissions).map(([role, data]) => (
                <div key={role} className="valhalla-card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <RoleBadge role={role} />
                    <span className="text-[#9CA3AF] text-sm">{roleCounts[role] || 0} {isSpanish ? 'usuarios' : 'users'}</span>
                  </div>
                  <h4 className="text-[#F3F4F6] font-semibold mb-2">{data.name}</h4>
                  <p className="text-sm text-[#9CA3AF] mb-4">{data.description}</p>
                  <div className="border-t border-[#242E42] pt-4">
                    <p className="text-xs text-[#6B7280] mb-2">{isSpanish ? 'Permisos principales:' : 'Main permissions:'}</p>
                    <div className="flex flex-wrap gap-1">
                      {data.permissions.slice(0, 8).map(perm => (
                        <span key={perm} className="text-xs px-2 py-1 bg-[#0B0E14] rounded text-[#9CA3AF]">
                          {perm.split('.')[0]}
                        </span>
                      ))}
                      {data.permissions.length > 8 && (
                        <span className="text-xs px-2 py-1 bg-[#E2B05E]/20 rounded text-[#E2B05E]">
                          +{data.permissions.length - 8}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <UserFormDialog
        user={editingUser}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={fetchData}
      />
    </Layout>
  );
}

export default UsersPage;
